/**
 * My Profile — CHI Alliance
 * Member command center for profile, directory visibility, and account security.
 */
import { useState, useEffect, useRef, type CSSProperties } from "react";
import { Link, useLocation } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { MemberProfilePhoto } from "@/components/PersonPortraitAvatar";
import DirectoryPrivacyControls from "@/components/DirectoryPrivacyControls";
import PasskeySecurityCard from "@/components/PasskeySecurityCard";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Edit3,
  Save,
  X,
  Tag,
  Linkedin,
  CheckCircle,
  ArrowRight,
  Eye,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  Crown,
  CalendarClock,
} from "lucide-react";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";
import { formatPhoneForDisplay } from "@shared/profileInputs";
import {
  PhoneInput,
  LinkedInInput,
  storedLinkedInValue,
  LINKEDIN_INPUT_HINT,
  splitStoredPhone,
  draftPhoneValue,
  storedPhoneValue,
  linkedinUsernameFromStored,
} from "@/components/ProfileContactInputs";
import { FadeUp } from "@/components/ScrollAnimations";
import type { DirectoryPrivacyPreferences } from "@shared/directoryPrivacy";

const INTEREST_OPTIONS = [
  "Leadership", "AI in HR", "Talent Strategy", "DEI", "Workforce Planning",
  "Organizational Development", "Employee Experience", "Compensation & Benefits",
  "HR Technology", "Coaching", "Change Management", "Recruiting",
];

const panelStyle: CSSProperties = {
  position: "relative",
  overflow: "hidden",
  background: "linear-gradient(145deg, rgba(22,30,54,0.9), rgba(8,13,29,0.86) 58%, rgba(29,12,43,0.78))",
  border: "1px solid rgba(191,201,255,0.13)",
  borderRadius: "1.35rem",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07), 0 22px 54px rgba(0,0,0,0.23)",
  backdropFilter: "blur(22px)",
  WebkitBackdropFilter: "blur(22px)",
};

const fieldIconStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "0.55rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "#80E8FF",
  background: "rgba(0,212,255,0.09)",
  border: "1px solid rgba(0,212,255,0.18)",
};

function PanelTitle({ icon, eyebrow, title, description }: { icon: React.ReactNode; eyebrow: string; title: string; description?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.8rem", marginBottom: description ? "1.2rem" : "1.4rem" }}>
      <span style={{ ...fieldIconStyle, width: 36, height: 36, color: "#F3B4E1", background: "rgba(233,30,140,0.1)", border: "1px solid rgba(233,30,140,0.2)" }} aria-hidden="true">
        {icon}
      </span>
      <div>
        <p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#7D8AA8" }}>
          {eyebrow}
        </p>
        <h2 style={{ margin: "0.18rem 0 0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.04rem", fontWeight: 700, color: "#F8FAFC", letterSpacing: "-0.01em" }}>
          {title}
        </h2>
        {description && <p style={{ margin: "0.36rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{description}</p>}
      </div>
    </div>
  );
}

/**
 * How each access state is described to the member whose membership it is.
 *
 * `cancelled` and `expired` are separated from `inactive` because they are not
 * the same situation: someone who never joined is being invited, while someone
 * whose membership ended is being told it ended.
 */
const MEMBERSHIP_STATE_COPY: Record<string, { label: string; tone: string; detail: string }> = {
  active: { label: "Active", tone: "#86EFAC", detail: "Your membership is current." },
  past_due: { label: "Payment overdue", tone: "#FCD34D", detail: "The last payment did not go through. Update your card to keep your access." },
  cancelled: { label: "Cancelled", tone: "#FCA5A5", detail: "Your membership has been cancelled." },
  expired: { label: "Expired", tone: "#FCA5A5", detail: "Your membership has ended." },
  inactive: { label: "Free account", tone: "#94A3B8", detail: "You are on a free account. Activate free membership to open the full directory and member events." },
};

function formatRenewalDate(value: string | Date): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(date);
}

/**
 * Membership state and, when there is one, the date the billing period ends.
 *
 * The date is labelled by what it will actually do. The same timestamp is a
 * renewal when billing continues and a last day of access when the subscription
 * is set to stop, so `cancelAtPeriodEnd` decides the wording — telling someone
 * who has cancelled that they "renew automatically" would be wrong in the one
 * direction that costs them money.
 */
function MembershipStatusPanel({
  entitlement,
  loading,
  isAdministrator,
  panelStyle,
  fieldIconStyle,
}: {
  entitlement: {
    accessState: string;
    currentPeriodEnd?: string | Date | null;
    cancelAtPeriodEnd?: number | null;
  } | null;
  loading: boolean;
  isAdministrator: boolean;
  panelStyle: React.CSSProperties;
  fieldIconStyle: React.CSSProperties;
}) {
  const state = entitlement?.accessState ?? "inactive";
  const copy = MEMBERSHIP_STATE_COPY[state] ?? MEMBERSHIP_STATE_COPY.inactive;
  const billing = state === "active" || state === "past_due";
  const willCancel = Boolean(entitlement?.cancelAtPeriodEnd);
  const periodEnd = entitlement?.currentPeriodEnd ? formatRenewalDate(entitlement.currentPeriodEnd) : null;

  return (
    <section style={{ ...panelStyle, padding: "1.1rem", background: "radial-gradient(circle at 84% 10%, rgba(233,30,140,0.16), transparent 30%), linear-gradient(145deg, rgba(38,20,45,0.92), rgba(10,13,28,0.9))" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.9rem" }}>
        <span style={{ ...fieldIconStyle, color: "#F9A8D4", background: "rgba(233,30,140,0.12)", borderColor: "rgba(233,30,140,0.28)" }}><Crown size={15} /></span>
        <div>
          <p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.64rem", color: "#71809F", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>{isAdministrator ? "Site access" : "Membership"}</p>
          <h2 style={{ margin: "0.18rem 0 0", fontFamily: "'Space Grotesk', sans-serif", color: "white", fontSize: "1rem" }}>{isAdministrator ? "Administrator" : "Subscription"}</h2>
        </div>
      </div>

      {isAdministrator ? (
        <>
          <p style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.45rem", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 750, fontSize: "0.86rem", color: "#D8B4FE" }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: "#A855F7", boxShadow: "0 0 10px #A855F7" }} />
            Full site access
          </p>
          <p style={{ margin: "0.4rem 0 0", color: "#A5B1C6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            Your administrator role includes member-level content access and administrative tools. Membership billing does not affect your site access.
          </p>
        </>
      ) : loading ? (
        <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }}>Checking your membership…</p>
      ) : (
        <>
          <p style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.45rem", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 750, fontSize: "0.86rem", color: copy.tone }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: copy.tone, boxShadow: `0 0 10px ${copy.tone}` }} />
            {copy.label}
          </p>
          <p style={{ margin: "0.4rem 0 0", color: "#A5B1C6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            {copy.detail}
          </p>

          {billing && periodEnd && (
            <p style={{ margin: "0.85rem 0 0", display: "flex", alignItems: "flex-start", gap: "0.45rem", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
              <CalendarClock size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: "0.15rem", color: willCancel ? "#FCA5A5" : "#86EFAC" }} />
              <span>
                {willCancel
                  ? <>Your membership will <strong style={{ color: "#FCA5A5" }}>not renew</strong>. Access continues until <strong style={{ color: "white" }}>{periodEnd}</strong>.</>
                  : <>Renews automatically on <strong style={{ color: "white" }}>{periodEnd}</strong>.</>}
              </span>
            </p>
          )}

          {/*
            Only said when there is genuinely nothing to show. An active
            membership whose period has not synced yet gets silence rather than a
            guessed date.
          */}
          {billing && !periodEnd && state === "past_due" && (
            <p style={{ margin: "0.85rem 0 0", color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-dense)", lineHeight: 1.5 }}>
              Your next billing date will appear here once Stripe confirms the current period.
            </p>
          )}

          {!billing && (
            <Link
              href="/membership"
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", marginTop: "0.9rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.78rem", textDecoration: "none" }}
            >
              {state === "inactive" ? "Join free" : "Review your membership"} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          )}
        </>
      )}
    </section>
  );
}

export default function MyProfile() {
  const { isLoggedIn, user, updateProfile } = useAuth();
  const [, navigate] = useLocation();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const privacyQuery = trpc.members.myDirectoryPrivacy.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const storedProfileQuery = trpc.members.myProfile.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const utils = trpc.useUtils();
  const updatePrivacy = trpc.members.updateMyDirectoryPrivacy.useMutation();
  const updateProfileMutation = trpc.members.updateMyProfile.useMutation();
  const updateMessagingConsent = trpc.members.updateMyMessagingConsent.useMutation();
  const uploadPhoto = trpc.members.uploadMyPhoto.useMutation();
  const entitlementQuery = trpc.members.myEntitlement.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoDragActive, setPhotoDragActive] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);
  const [directoryPreferences, setDirectoryPreferences] = useState<DirectoryPrivacyPreferences | null>(null);
  const [directorySaveState, setDirectorySaveState] = useState<"idle" | "saved" | "error">("idle");
  const [messageOptIn, setMessageOptIn] = useState(false);
  const [messageConsentSaveState, setMessageConsentSaveState] = useState<"idle" | "saved" | "error">("idle");
  /** What the phone field shows; form.phone keeps the stored E.164 value. */
  const [phoneDraft, setPhoneDraft] = useState<{ countryCode: string; national: string }>({ countryCode: "+1", national: "" });
  const [form, setForm] = useState({
    name: user?.name ?? "", email: user?.email ?? "", title: user?.title ?? "", company: user?.company ?? "",
    bio: user?.bio ?? "", phone: user?.phone ?? "", linkedin: user?.linkedin ?? "", interests: user?.interests ?? [],
  });

  useEffect(() => {
    if (!user) return;
    setPhoneDraft(splitStoredPhone(user.phone));
    setForm({ name: user.name, email: user.email, title: user.title, company: user.company, bio: user.bio, phone: user.phone, linkedin: user.linkedin, interests: user.interests });
  }, [user]);

  useEffect(() => {
    if (!storedProfileQuery.data || editing) return;
    const stored = storedProfileQuery.data;
    setPhoneDraft(splitStoredPhone(stored.phone));
    setForm({
      name: stored.fullName, email: stored.email, title: stored.professionalTitle ?? "", company: stored.company ?? "",
      bio: stored.bio ?? "", phone: stored.phone ?? "", linkedin: stored.linkedinUrl ?? "", interests: stored.interests,
    });
  }, [storedProfileQuery.data, editing]);

  useEffect(() => {
    if (storedProfileQuery.data) setMessageOptIn(storedProfileQuery.data.messageOptIn);
  }, [storedProfileQuery.data]);

  useEffect(() => {
    if (privacyQuery.data) setDirectoryPreferences(privacyQuery.data.preferences);
  }, [privacyQuery.data]);

  const storedPhotoUrl = storedProfileQuery.data?.photoUrl ?? null;
  const displayPhotoUrl = storedProfileQuery.data?.displayPhotoUrl ?? storedPhotoUrl;

  const handlePhotoFile = (file: File) => {
    setPhotoError(null);
    const extension = file.name.split(".").pop()?.toLowerCase();
    const isSupportedType = ["image/png", "image/jpeg", "image/webp"].includes(file.type)
      || (!file.type && ["png", "jpg", "jpeg", "webp"].includes(extension ?? ""));
    if (!isSupportedType) {
      setPhotoError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setPhotoError(`That image is larger than ${MAX_IMAGE_UPLOAD_LABEL}. Choose a smaller one.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      if (typeof reader.result !== "string") return;
      try {
        const detectedType = file.type || (
          extension === "png" ? "image/png"
            : extension === "webp" ? "image/webp"
              : "image/jpeg"
        );
        const dataUrl = reader.result.replace(/^data:;base64,/i, `data:${detectedType};base64,`);
        await uploadPhoto.mutateAsync({ dataUrl });
        await utils.members.myProfile.invalidate();
      } catch (error) {
        setPhotoError(error instanceof Error ? error.message : "That photo could not be saved.");
      }
    };
    reader.onerror = () => setPhotoError("That image could not be read. Try another file.");
    reader.readAsDataURL(file);
  };

  if (!isLoggedIn || !user) {
    return (
      <div style={{ background: "#060A16", minHeight: "100vh" }}>
        <Navigation />
        <section style={{ paddingTop: "clamp(96px, 14vw, 128px)", paddingBottom: "5rem", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
          <div style={{ textAlign: "center", maxWidth: "480px", padding: "0 1.5rem" }}>
            <div style={{ width: 72, height: 72, borderRadius: "1.25rem", background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}><User size={32} style={{ color: "#00D4FF" }} /></div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.5rem", color: "white", marginBottom: "1rem", lineHeight: 1.1 }}>My Profile</h1>
            <p style={{ color: "#94A3B8", fontSize: "1rem", lineHeight: 1.75, fontFamily: "'Inter', sans-serif", marginBottom: "2rem" }}>Sign in or create a free account to manage your CHI Alliance member profile.</p>
            <button onClick={() => navigate("/login")} style={{ display: "inline-flex", alignItems: "center", gap: "0.625rem", padding: "0.875rem 2rem", background: "linear-gradient(135deg, #00D4FF, #0099BB)", border: "none", borderRadius: "0.625rem", color: "#080C18", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.04em", textTransform: "uppercase", cursor: "pointer", boxShadow: "0 8px 24px rgba(0,212,255,0.3)" }}>Sign In to View Profile <ArrowRight size={16} /></button>
            <div style={{ marginTop: "1.25rem" }}><a href="/" style={{ color: "#64748B", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif", textDecoration: "underline" }}>Back to Home →</a></div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const handleSave = async () => {
    setProfileSaveError(null);
    try {
      const savedProfile = await updateProfileMutation.mutateAsync({ fullName: form.name, professionalTitle: form.title, company: form.company, bio: form.bio, phone: form.phone, linkedinUrl: storedLinkedInValue(form.linkedin) ?? "", interests: form.interests });
      updateProfile({ name: savedProfile.fullName, title: savedProfile.professionalTitle ?? "", company: savedProfile.company ?? "", bio: savedProfile.bio ?? "", phone: savedProfile.phone ?? "", linkedin: savedProfile.linkedinUrl ?? "", interests: savedProfile.interests });
      await utils.members.myProfile.invalidate();
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      setProfileSaveError(error instanceof Error ? error.message : "Your profile could not be saved.");
    }
  };

  const handleCancel = () => {
    setPhoneDraft(splitStoredPhone(user.phone));
    setForm({ name: user.name, email: user.email, title: user.title, company: user.company, bio: user.bio, phone: user.phone, linkedin: user.linkedin, interests: user.interests });
    setEditing(false);
  };

  const toggleInterest = (interest: string) => setForm((previous) => ({ ...previous, interests: previous.interests.includes(interest) ? previous.interests.filter((item) => item !== interest) : [...previous.interests, interest] }));

  const saveDirectoryPreferences = async () => {
    if (!directoryPreferences) return;
    setDirectorySaveState("idle");
    try {
      const savedSettings = await updatePrivacy.mutateAsync(directoryPreferences);
      setDirectoryPreferences(savedSettings.preferences);
      setDirectorySaveState("saved");
    } catch {
      setDirectorySaveState("error");
    }
  };

  const saveMessagingConsent = async () => {
    const nextValue = !messageOptIn;
    setMessageConsentSaveState("idle");
    try {
      const savedPreference = await updateMessagingConsent.mutateAsync({ messageOptIn: nextValue });
      setMessageOptIn(savedPreference.messageOptIn);
      setMessageConsentSaveState("saved");
      await utils.members.myProfile.invalidate();
    } catch {
      setMessageConsentSaveState("error");
    }
  };

  const inputStyle = (editable: boolean): CSSProperties => ({
    width: "100%", background: editable ? "rgba(6,12,29,0.62)" : "rgba(255,255,255,0.025)",
    border: editable ? "1px solid rgba(233,30,140,0.44)" : "1px solid rgba(255,255,255,0.08)", borderRadius: "0.75rem",
    padding: "0.78rem 0.95rem", color: editable ? "#F8FAFC" : "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "0.88rem",
    outline: "none", transition: "border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease", cursor: editable ? "text" : "default",
  });

  const labelStyle: CSSProperties = { fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#71809F", marginBottom: "0.46rem", display: "flex", alignItems: "center", gap: "0.42rem" };
  const directoryLabel = directoryPreferences?.directoryVisible ? "Sharing selected details" : directoryPreferences ? "Private by choice" : "Loading preferences";
  const titleLine = [form.title, form.company].filter(Boolean).join(" · ") || "CHI Alliance member workspace";

  return (
    <div style={{ minHeight: "100vh", color: "#F8FAFC", background: "radial-gradient(circle at 76% 6%, rgba(0,212,255,0.13), transparent 22%), radial-gradient(circle at 16% 17%, rgba(168,85,247,0.2), transparent 25%), radial-gradient(circle at 72% 74%, rgba(233,30,140,0.13), transparent 28%), #060A16" }}>
      <Navigation />
      <main style={{ paddingTop: "106px", paddingBottom: "5.5rem" }}>
        <div className="container" style={{ maxWidth: 1320 }}>
          <FadeUp duration={700}>
            <section style={{ ...panelStyle, padding: "clamp(1.25rem, 3vw, 2rem)", marginBottom: "1.25rem", background: "radial-gradient(circle at 82% 18%, rgba(0,212,255,0.17), transparent 26%), radial-gradient(circle at 22% 100%, rgba(233,30,140,0.16), transparent 38%), linear-gradient(135deg, rgba(22,30,56,0.94), rgba(9,13,29,0.92) 58%, rgba(34,12,49,0.84))" }}>
              <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "34px 34px", maskImage: "linear-gradient(to bottom, rgba(0,0,0,.52), transparent 85%)" }} />
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between" style={{ position: "relative" }}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <button type="button" onClick={() => photoInputRef.current?.click()} onDragOver={event => { event.preventDefault(); setPhotoDragActive(true); }} onDragLeave={() => setPhotoDragActive(false)} onDrop={event => { event.preventDefault(); setPhotoDragActive(false); const file = event.dataTransfer.files?.[0]; if (file) handlePhotoFile(file); }} disabled={uploadPhoto.isPending} aria-label={storedPhotoUrl ? "Change your profile photo" : "Add a profile photo"} title={storedPhotoUrl ? "Change your profile photo" : "Add a profile photo"} style={{ width: 78, height: 78, padding: 0, borderRadius: "1.25rem", border: photoDragActive ? "2px dashed #7DEAFF" : "1px solid rgba(248,192,237,0.58)", background: displayPhotoUrl ? "#11182D" : "linear-gradient(145deg, #E91E8C, #8B5CF6 52%, #00D4FF)", display: "grid", placeItems: "center", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.9rem", color: "white", flexShrink: 0, overflow: "hidden", cursor: uploadPhoto.isPending ? "wait" : "pointer", boxShadow: "0 0 0 5px rgba(233,30,140,0.1), 0 18px 42px rgba(0,0,0,0.36)" }}>
                    {displayPhotoUrl ? (
                      <MemberProfilePhoto
                        name={storedProfileQuery.data?.fullName || user.name || "CHI Alliance Member"}
                        speakerPortraitUrl={displayPhotoUrl}
                        memberPhotoUrl={storedPhotoUrl}
                        size={78}
                        accent="#F9A8D4"
                      />
                    ) : user.avatar}
                  </button>
                   <input ref={photoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) handlePhotoFile(file); }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.69rem", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#8CEAFF" }}>Your member space</p>
                    <h1 style={{ margin: "0.32rem 0 0", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.25rem, 5vw, 3.75rem)", lineHeight: 0.94, letterSpacing: "0.02em", color: "#FFFFFF" }}>{user.name}</h1>
                    <p style={{ margin: "0.55rem 0 0", color: "#B5C0D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.86rem", lineHeight: 1.45 }}>{titleLine}</p>
                     <p style={{ margin: "0.38rem 0 0", color: uploadPhoto.isPending ? "#67E8F9" : "#71809F", fontFamily: "'Inter', sans-serif", fontSize: "0.72rem" }}>{uploadPhoto.isPending ? "Saving your photo…" : storedPhotoUrl ? "Select or drop a photo to update it" : "Select or drop a photo to add it"}</p>
                    {photoError && <p role="alert" style={{ color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", margin: "0.38rem 0 0" }}>{photoError}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {saved && <span role="status" style={{ display: "flex", alignItems: "center", gap: "0.42rem", color: "#73E3B4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.78rem", fontWeight: 700 }}><CheckCircle size={15} /> Changes saved</span>}
                  {editing ? <>
                    <button onClick={handleCancel} style={{ display: "inline-flex", alignItems: "center", gap: "0.42rem", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "#C7D2E3", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.78rem", padding: "0.68rem 1rem", borderRadius: "0.72rem", cursor: "pointer" }}><X size={15} /> Cancel</button>
                    <button onClick={handleSave} disabled={updateProfileMutation.isPending} style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: "linear-gradient(135deg, #E91E8C, #A855F7 60%, #6D5EF6)", border: "1px solid rgba(255,255,255,0.22)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: "0.78rem", padding: "0.68rem 1rem", borderRadius: "0.72rem", cursor: updateProfileMutation.isPending ? "wait" : "pointer", opacity: updateProfileMutation.isPending ? 0.65 : 1, boxShadow: "0 12px 26px rgba(130,26,129,0.32)" }}><Save size={15} /> {updateProfileMutation.isPending ? "Saving…" : "Save profile"}</button>
                    {profileSaveError && <p role="alert" style={{ flexBasis: "100%", margin: 0, color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.74rem" }}>{profileSaveError}</p>}
                  </> : <button onClick={() => setEditing(true)} style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: "rgba(0,212,255,0.09)", border: "1px solid rgba(0,212,255,0.32)", color: "#9DEFFF", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: "0.78rem", padding: "0.7rem 1rem", borderRadius: "0.72rem", cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)" }}><Edit3 size={15} /> Edit profile</button>}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" style={{ position: "relative", marginTop: "1.45rem" }}>
                {[
                  { icon: <Sparkles size={15} />, label: "Profile status", value: editing ? "Editing in progress" : "Ready to personalize", accent: "#D8A4FF" },
                  { icon: <Eye size={15} />, label: "Directory", value: directoryLabel, accent: "#81E6FF" },
                  { icon: <ShieldCheck size={15} />, label: "Account security", value: "Manage protection", accent: "#86F0C2" },
                  { icon: <Mail size={15} />, label: "Community updates", value: messageOptIn ? "Opted in" : "Transactional only", accent: "#FDBA74" },
                ].map((item) => <div key={item.label} style={{ minHeight: 74, display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.8rem 0.9rem", borderRadius: "0.9rem", background: "rgba(4,10,26,0.38)", border: "1px solid rgba(255,255,255,0.09)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}><span style={{ ...fieldIconStyle, color: item.accent, background: `${item.accent}14`, borderColor: `${item.accent}2E` }}>{item.icon}</span><span><span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.64rem", fontWeight: 700, color: "#71809F", letterSpacing: "0.09em", textTransform: "uppercase" }}>{item.label}</span><span style={{ display: "block", marginTop: "0.18rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 650, color: "#E7EDF8" }}>{item.value}</span></span></div>)}
              </div>
            </section>
          </FadeUp>

          <FadeUp duration={700} delay={80}>
            <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.22fr)_minmax(330px,0.78fr)]">
              <div className="flex flex-col gap-5">
                <section style={{ ...panelStyle, padding: "clamp(1.1rem, 2.5vw, 1.75rem)" }}>
                  <PanelTitle icon={<User size={17} />} eyebrow="Identity" title="Personal information" description={editing ? "Update the details that describe your member profile." : "Your core member details, kept in one clear view."} />
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {[
                      { key: "name", label: "Full name", icon: <User size={13} />, type: "text" }, { key: "email", label: "Email address", icon: <Mail size={13} />, type: "email" },
                      { key: "title", label: "Role", icon: <Briefcase size={13} />, type: "text" }, { key: "company", label: "Organization", icon: <Building2 size={13} />, type: "text" },
                    ].map(({ key, label, icon, type }) => <div key={key} style={{ padding: "0.72rem 0.75rem 0.78rem", borderRadius: "0.82rem", background: "rgba(255,255,255,0.022)", border: "1px solid rgba(255,255,255,0.055)" }}><label style={labelStyle}>{icon} {label}</label><input type={type} value={form[key as keyof typeof form] as string} onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))} disabled={!editing} style={inputStyle(editing)} /></div>)}
                  </div>

                  {/*
                    The same phone and LinkedIn controls as the create-account flow
                    and the admin CRM. These were plain text inputs here, so a
                    member could type a number in any shape and paste anything at
                    all as a LinkedIn URL — the two fields the rest of the product
                    is careful about were the two this page was not.

                    Read-only mode shows the stored values formatted, since the
                    controls are for editing and a disabled combobox reads as
                    broken rather than locked.
                  */}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2" style={{ marginTop: "0.75rem" }}>
                    <div style={{ padding: "0.72rem 0.75rem 0.78rem", borderRadius: "0.82rem", background: "rgba(255,255,255,0.022)", border: "1px solid rgba(255,255,255,0.055)" }}>
                      <label style={labelStyle} htmlFor="my-profile-phone"><Phone size={13} /> Phone number</label>
                      {editing ? (
                        <PhoneInput
                          id="my-profile-phone"
                          countryCode={phoneDraft.countryCode}
                          value={phoneDraft.national}
                          onChange={next => {
                            setPhoneDraft(next.countryCode === phoneDraft.countryCode ? { ...phoneDraft, national: next.value } : { countryCode: next.countryCode, national: next.value });
                            setForm(previous => ({ ...previous, phone: draftPhoneValue(next.countryCode, next.value) ?? "" }));
                          }}
                        />
                      ) : (
                        <input readOnly value={formatPhoneForDisplay(form.phone) ?? ""} style={inputStyle(false)} />
                      )}
                    </div>

                    <div style={{ padding: "0.72rem 0.75rem 0.78rem", borderRadius: "0.82rem", background: "rgba(255,255,255,0.022)", border: "1px solid rgba(255,255,255,0.055)" }}>
                      <label style={labelStyle} htmlFor="my-profile-linkedin"><Linkedin size={13} /> LinkedIn</label>
                      {editing ? (
                        <>
                          <LinkedInInput
                            id="my-profile-linkedin"
                            value={linkedinUsernameFromStored(form.linkedin)}
                            onChange={username => setForm(previous => ({ ...previous, linkedin: username }))}
                          />
                          <p style={{ margin: "0.35rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-dense)" }}>{LINKEDIN_INPUT_HINT}</p>
                        </>
                      ) : (
                        <input readOnly value={form.linkedin} style={inputStyle(false)} />
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: "0.9rem", padding: "0.75rem", borderRadius: "0.82rem", background: "rgba(255,255,255,0.022)", border: "1px solid rgba(255,255,255,0.055)" }}><label style={labelStyle}><Edit3 size={13} /> About you</label><textarea value={form.bio} onChange={(event) => setForm((previous) => ({ ...previous, bio: event.target.value }))} disabled={!editing} rows={5} style={{ ...inputStyle(editing), resize: editing ? "vertical" : "none", lineHeight: 1.6 }} /></div>
                </section>

                <section style={{ ...panelStyle, padding: "clamp(1.1rem, 2.5vw, 1.75rem)", background: "radial-gradient(circle at 88% 10%, rgba(0,212,255,0.11), transparent 29%), linear-gradient(145deg, rgba(20,31,55,0.9), rgba(8,13,29,0.86))" }}>
                  <PanelTitle icon={<Tag size={17} />} eyebrow="Focus" title="Topics that move you" description={editing ? "Choose the conversations that are most useful to you." : "Your selected interest areas across the CHI Alliance community."} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.55rem" }}>{INTEREST_OPTIONS.map((interest) => { const selected = form.interests.includes(interest); return <button key={interest} onClick={() => editing && toggleInterest(interest)} disabled={!editing} style={{ padding: "0.48rem 0.78rem", borderRadius: "999px", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.73rem", fontWeight: 650, cursor: editing ? "pointer" : "default", transition: "transform 0.18s ease, border-color 0.18s ease, background 0.18s ease", background: selected ? "linear-gradient(135deg, rgba(0,212,255,0.16), rgba(168,85,247,0.18))" : "rgba(255,255,255,0.035)", border: selected ? "1px solid rgba(0,212,255,0.42)" : "1px solid rgba(255,255,255,0.1)", color: selected ? "#9DEFFF" : "#9BA9C0" }}>{selected ? "✓ " : ""}{interest}</button>; })}</div>
                </section>

                <section style={{ ...panelStyle, padding: "clamp(1.1rem, 2.5vw, 1.75rem)", background: "radial-gradient(circle at 8% 88%, rgba(233,30,140,0.12), transparent 28%), linear-gradient(145deg, rgba(20,29,53,0.9), rgba(10,13,28,0.89))" }}>
                  <PanelTitle icon={<Eye size={17} />} eyebrow="Community visibility" title="Member directory privacy" description="Choose the profile details you want other CHI Alliance members to see." />
                  {privacyQuery.isLoading && <p style={{ color: "#9FAEC5", fontFamily: "'Inter', sans-serif", fontSize: ".82rem", margin: 0 }}>Loading your directory preferences…</p>}
                  {privacyQuery.isError && <p role="alert" style={{ color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".8rem", margin: 0 }}>Your directory preferences could not be loaded. Refresh the page to try again.</p>}
                  {directoryPreferences && privacyQuery.data && <><DirectoryPrivacyControls value={directoryPreferences} onChange={(next) => { setDirectoryPreferences(next); setDirectorySaveState("idle"); }} preview={privacyQuery.data.preview} disabled={updatePrivacy.isPending} /><div className="flex flex-wrap items-center justify-end gap-3" style={{ marginTop: "1rem" }}>{directorySaveState === "saved" && <span role="status" style={{ color: "#73E3B4", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>Directory preferences saved.</span>}{directorySaveState === "error" && <span role="alert" style={{ color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>Preferences could not be saved. Try again.</span>}<button type="button" onClick={saveDirectoryPreferences} disabled={updatePrivacy.isPending} style={{ padding: ".7rem 1rem", border: "1px solid rgba(255,255,255,0.2)", borderRadius: ".7rem", background: "linear-gradient(135deg,#E91E8C,#8B5CF6)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: ".75rem", cursor: updatePrivacy.isPending ? "wait" : "pointer", opacity: updatePrivacy.isPending ? .65 : 1, boxShadow: "0 10px 20px rgba(113,15,96,0.26)" }}>{updatePrivacy.isPending ? "Saving…" : "Save visibility"}</button></div></>}
                </section>

                <section style={{ ...panelStyle, padding: "clamp(1.1rem, 2.5vw, 1.75rem)", background: "radial-gradient(circle at 92% 12%, rgba(251,186,116,0.12), transparent 30%), linear-gradient(145deg, rgba(31,28,49,0.9), rgba(10,13,28,0.89))" }}>
                  <PanelTitle icon={<Mail size={17} />} eyebrow="Communications" title="Community updates" description="Choose whether to receive optional CHI Alliance news and member announcements. Transactional account messages are not affected." />
                  <button type="button" role="switch" aria-checked={messageOptIn} aria-label="Community updates" onClick={saveMessagingConsent} disabled={updateMessagingConsent.isPending} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "0.95rem 1rem", borderRadius: "0.9rem", textAlign: "left", background: messageOptIn ? "linear-gradient(135deg, rgba(0,212,255,0.12), rgba(168,85,247,0.16))" : "rgba(255,255,255,0.035)", border: messageOptIn ? "1px solid rgba(0,212,255,0.38)" : "1px solid rgba(255,255,255,0.12)", color: "#F8FAFC", cursor: updateMessagingConsent.isPending ? "wait" : "pointer", opacity: updateMessagingConsent.isPending ? 0.68 : 1, transition: "background 0.18s ease, border-color 0.18s ease, transform 0.18s ease" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.72rem" }}><span style={{ ...fieldIconStyle, color: messageOptIn ? "#8CEAFF" : "#94A3B8", background: messageOptIn ? "rgba(0,212,255,0.1)" : "rgba(255,255,255,0.05)", borderColor: messageOptIn ? "rgba(0,212,255,0.24)" : "rgba(255,255,255,0.1)" }}><Mail size={14} /></span><span><strong style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.84rem", letterSpacing: "-0.01em" }}>{messageOptIn ? "You are subscribed" : "Optional updates are off"}</strong><span style={{ display: "block", marginTop: "0.18rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{messageOptIn ? "Select to stop optional community messages." : "Select to opt in to optional community messages."}</span></span></span>
                    <span aria-hidden="true" style={{ width: 42, height: 24, display: "inline-flex", alignItems: "center", justifyContent: messageOptIn ? "flex-end" : "flex-start", padding: 3, borderRadius: 999, background: messageOptIn ? "linear-gradient(135deg, #00D4FF, #8B5CF6)" : "rgba(148,163,184,0.25)", boxShadow: messageOptIn ? "0 0 18px rgba(0,212,255,0.25)" : "none", flexShrink: 0 }}><span style={{ width: 18, height: 18, borderRadius: "50%", background: "#F8FAFC", boxShadow: "0 2px 6px rgba(0,0,0,0.24)" }} /></span>
                  </button>
                  {messageConsentSaveState === "saved" && <p role="status" style={{ margin: "0.72rem 0 0", color: "#73E3B4", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>Your community update preference has been saved.</p>}
                  {messageConsentSaveState === "error" && <p role="alert" style={{ margin: "0.72rem 0 0", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>Your communications preference could not be saved. Try again.</p>}
                </section>
              </div>

              <aside className="flex flex-col gap-5">
                <MembershipStatusPanel
                  entitlement={entitlementQuery.data ?? null}
                  loading={entitlementQuery.isLoading}
                  isAdministrator={user.role === "admin"}
                  panelStyle={panelStyle}
                  fieldIconStyle={fieldIconStyle}
                />

                <section style={{ ...panelStyle, padding: "1.1rem", background: "radial-gradient(circle at 82% 8%, rgba(168,85,247,0.17), transparent 28%), linear-gradient(145deg, rgba(24,27,57,0.91), rgba(9,12,26,0.91))" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.92rem" }}><span style={{ ...fieldIconStyle, color: "#DDB4FF", background: "rgba(168,85,247,0.12)", borderColor: "rgba(168,85,247,0.28)" }}><LockKeyhole size={15} /></span><div><p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.64rem", color: "#71809F", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>Protected access</p><h2 style={{ margin: "0.18rem 0 0", fontFamily: "'Space Grotesk', sans-serif", color: "white", fontSize: "1rem" }}>Security center</h2></div></div>
                  <PasskeySecurityCard />
                </section>
              </aside>
            </section>
          </FadeUp>

          <FadeUp duration={700} delay={140}>
            <section style={{ ...panelStyle, marginTop: "1.25rem", padding: "1.15rem 1.25rem", background: "linear-gradient(145deg, rgba(64,17,37,0.35), rgba(24,12,27,0.65))", border: "1px solid rgba(251,113,133,0.19)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
              <div className="flex items-start gap-3"><span style={{ ...fieldIconStyle, color: "#FF9CAD", background: "rgba(251,113,133,0.09)", borderColor: "rgba(251,113,133,0.24)" }}><LockKeyhole size={15} /></span><div><p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: "0.88rem", color: "#FFB4C2" }}>Delete account</p><p style={{ margin: "0.25rem 0 0", color: "#A5B1C6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Permanently remove your account and all associated data.</p></div></div>
              <button style={{ background: "transparent", border: "1px solid rgba(251,113,133,0.36)", color: "#FFADB9", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 750, fontSize: "0.76rem", padding: "0.68rem 1rem", borderRadius: "0.7rem", cursor: "pointer" }}>Delete account</button>
            </section>
          </FadeUp>
        </div>
      </main>
      <Footer />
    </div>
  );
}
